import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import VideoManager from './VideoManager';
import PPTManager from './PPTManager';
import InfographicManager from './InfographicManager';
import QuizBuilder, { QuizQuestion } from './QuizBuilder';
import ManualManager from './ManualManager';
import WorkshopManager, { WorkshopVideo } from './WorkshopManager';
import { MODULES } from './ModuleSelector';

interface ModuleEditorProps {
  moduleId: string;
  onBack: () => void;
}

type TabId = 'video' | 'ppt' | 'infografia' | 'manual' | 'taller' | 'cuestionario';

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: 'video', label: 'Video', icon: 'ri-video-line' },
  { id: 'ppt', label: 'Presentación', icon: 'ri-slideshow-line' },
  { id: 'infografia', label: 'Infografía', icon: 'ri-image-2-line' },
  { id: 'manual', label: 'Manual', icon: 'ri-file-text-line' },
  { id: 'taller', label: 'Taller Práctico', icon: 'ri-play-circle-line' },
  { id: 'cuestionario', label: 'Cuestionario', icon: 'ri-questionnaire-line' },
];

interface ContentState {
  vimeo_id: string;
  ppt_embed_url: string;
  infographic_url: string;
  manual_url: string;
  workshop_videos: WorkshopVideo[];
  is_published: boolean;
}

export default function ModuleEditor({ moduleId, onBack }: ModuleEditorProps) {
  const [activeTab, setActiveTab] = useState<TabId>('video');
  const [content, setContent] = useState<ContentState>({
    vimeo_id: '',
    ppt_embed_url: '',
    infographic_url: '',
    manual_url: '',
    workshop_videos: [],
    is_published: false,
  });
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const moduleInfo = MODULES.find((m) => m.id === moduleId);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [contentRes, quizRes] = await Promise.all([
        supabase.from('module_content').select('*').eq('module_id', moduleId).maybeSingle(),
        supabase.from('quiz_questions').select('*').eq('module_id', moduleId).order('order_index'),
      ]);
      if (contentRes.data) {
        setContent({
          vimeo_id: contentRes.data.vimeo_id || '',
          ppt_embed_url: contentRes.data.ppt_embed_url || '',
          infographic_url: contentRes.data.infographic_url || '',
          manual_url: contentRes.data.manual_url || '',
          workshop_videos: contentRes.data.workshop_videos || [],
          is_published: contentRes.data.is_published || false,
        });
      }
      if (quizRes.data) {
        setQuestions(
          quizRes.data.map((q: {
            id: string;
            question_text: string;
            options: string[];
            correct_index: number;
            order_index: number;
            option_explanations?: string[];
          }) => ({
            id: q.id,
            question_text: q.question_text,
            options: q.options,
            correct_index: q.correct_index,
            order_index: q.order_index,
            option_explanations: q.option_explanations || [],
          }))
        );
      }
    } finally {
      setLoading(false);
    }
  }, [moduleId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      await supabase.from('module_content').update({
        vimeo_id: content.vimeo_id,
        ppt_embed_url: content.ppt_embed_url,
        infographic_url: content.infographic_url,
        manual_url: content.manual_url,
        workshop_videos: content.workshop_videos,
        is_published: content.is_published,
        updated_at: new Date().toISOString(),
      }).eq('module_id', moduleId);

      // Save quiz: delete all and re-insert
      await supabase.from('quiz_questions').delete().eq('module_id', moduleId);
      if (questions.length > 0) {
        await supabase.from('quiz_questions').insert(
          questions.map((q, i) => ({
            module_id: moduleId,
            question_text: q.question_text,
            options: q.options,
            correct_index: q.correct_index,
            order_index: i,
            option_explanations: q.option_explanations || [],
          }))
        );
      }
      setSaveMsg('Guardado correctamente');
    } catch {
      setSaveMsg('Error al guardar');
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(''), 3000);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#0D0F12' }}>
      {/* Top bar */}
      <div
        className="sticky top-0 z-30 px-6 py-4 flex items-center justify-between"
        style={{ background: 'rgba(13,15,18,0.95)', borderBottom: '1px solid rgba(248,246,243,0.07)', backdropFilter: 'blur(12px)' }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="w-9 h-9 flex items-center justify-center rounded-xl transition-all duration-200 hover:opacity-70 cursor-pointer"
            style={{ background: 'rgba(248,246,243,0.06)' }}
          >
            <i className="ri-arrow-left-line text-[#F8F6F3]/70 text-sm" />
          </button>
          <div>
            <p className="text-[#F8F6F3]/40 text-[10px] uppercase tracking-[2px]">Editando</p>
            <h2 className="text-[#F8F6F3] font-semibold text-sm tracking-tight">{moduleInfo?.name}</h2>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Published toggle */}
          <button
            onClick={() => setContent((prev) => ({ ...prev, is_published: !prev.is_published }))}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-medium cursor-pointer transition-all duration-200 whitespace-nowrap"
            style={content.is_published
              ? { background: 'rgba(139,157,131,0.15)', color: '#8B9D83', border: '1px solid rgba(139,157,131,0.25)' }
              : { background: 'rgba(248,246,243,0.06)', color: 'rgba(248,246,243,0.40)', border: '1px solid rgba(248,246,243,0.10)' }
            }
          >
            <i className={content.is_published ? 'ri-eye-line' : 'ri-eye-off-line'} />
            {content.is_published ? 'Publicado' : 'Borrador'}
          </button>

          {/* Save */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold cursor-pointer transition-all duration-200 whitespace-nowrap disabled:opacity-50"
            style={{ background: 'rgba(139,157,131,0.90)', color: '#1A1C20' }}
          >
            {saving ? (
              <><i className="ri-loader-4-line animate-spin" /> Guardando...</>
            ) : (
              <><i className="ri-save-line" /> Guardar</>
            )}
          </button>

          {saveMsg && (
            <span className={`text-xs ${saveMsg.includes('Error') ? 'text-[#C97B63]' : 'text-[#8B9D83]'} flex items-center gap-1`}>
              <i className={saveMsg.includes('Error') ? 'ri-error-warning-line' : 'ri-checkbox-circle-line'} />
              {saveMsg}
            </span>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line animate-spin text-[#8B9D83] text-2xl" />
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="flex gap-2 mb-8 flex-wrap">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium cursor-pointer transition-all duration-200 whitespace-nowrap"
                  style={activeTab === tab.id
                    ? { background: 'rgba(139,157,131,0.15)', color: '#8B9D83', border: '1px solid rgba(139,157,131,0.30)' }
                    : { background: 'rgba(248,246,243,0.04)', color: 'rgba(248,246,243,0.40)', border: '1px solid rgba(248,246,243,0.08)' }
                  }
                >
                  <i className={tab.icon} />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div
              className="rounded-2xl p-7"
              style={{ background: 'rgba(248,246,243,0.03)', border: '1px solid rgba(248,246,243,0.07)' }}
            >
              {activeTab === 'video' && (
                <VideoManager
                  vimeoId={content.vimeo_id}
                  onChange={(id) => setContent((prev) => ({ ...prev, vimeo_id: id }))}
                />
              )}
              {activeTab === 'ppt' && (
                <PPTManager
                  embedUrl={content.ppt_embed_url}
                  moduleId={moduleId}
                  onChange={(url) => setContent((prev) => ({ ...prev, ppt_embed_url: url }))}
                />
              )}
              {activeTab === 'infografia' && (
                <InfographicManager
                  imageUrl={content.infographic_url}
                  onChange={(url) => setContent((prev) => ({ ...prev, infographic_url: url }))}
                />
              )}
              {activeTab === 'manual' && (
                <ManualManager
                  manualUrl={content.manual_url}
                  moduleId={moduleId}
                  onChange={(url) => setContent((prev) => ({ ...prev, manual_url: url }))}
                />
              )}
              {activeTab === 'taller' && (
                <WorkshopManager
                  videos={content.workshop_videos}
                  onChange={(videos) => setContent((prev) => ({ ...prev, workshop_videos: videos }))}
                />
              )}
              {activeTab === 'cuestionario' && (
                <QuizBuilder
                  questions={questions}
                  onChange={setQuestions}
                />
              )}
            </div>

            {/* Bottom save */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-7 py-3 rounded-xl text-sm font-semibold cursor-pointer transition-all duration-200 whitespace-nowrap disabled:opacity-50"
                style={{ background: 'rgba(139,157,131,0.90)', color: '#1A1C20' }}
              >
                {saving ? 'Guardando...' : <><i className="ri-save-line mr-1" /> Guardar cambios</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
